import { useContext } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import MyTitle from "../../components/MyTitle";
import { useBoardItem } from "../../hooks/useBoardItem";
import MyLoading from "../../components/MyLoading";
import { AuthContext } from "../../context/AuthProvider";
import { PopupContext } from "../../context/PopupProvider";
import DeleteBoardItemDialog from "../Common/DeleteBoardItemDialog";
import MyButton from "../../components/MyButton";
import {
  BOARD_PERMISSION,
  NON_MEMBER_PERMISSIONS,
} from "../../constants/permission";
import { checkRoleAndPermissions } from "../../components/CheckRoleAndPermission";

const ContactUsDetail = ({ boardCode }) => {
  const { itemNo } = useParams();
  const { useGetBoard, useGetBoardItem } = useBoardItem();
  const { isAdmin, roleCode } = useContext(AuthContext);
  const { openPopup } = useContext(PopupContext);
  const navigate = useNavigate();

  const nonPermissions = NON_MEMBER_PERMISSIONS[boardCode];

  const { data: board } = useGetBoard({ boardCode }, (data) => data.data);

  const { data, isLoading, isError, error } = useGetBoardItem(
    {
      boardCode: board?.boardCode,
      itemNo,
      includeAdjacent: true,
      enabled: checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.READ,
        nonPermissions,
        isAdmin
      ),
    },
    (data) => data.data
  );

  const openDeleteDialog = () =>
    openPopup(
      <DeleteBoardItemDialog
        boardCode={board?.boardCode}
        itemNo={itemNo}
        onRedirect={() => {
          navigate("/ContactUs");
        }}
      />
    );

  return isAdmin ? (
    <>
      {isLoading ? (
        <MyLoading className="h-[150px]" />
      ) : isError && !data ? (
        <p className="text-center text-lg text-gray-dark pt-10">
          {error?.data?.code === "NO_PERMISSION"
            ? "이 글은 비밀글입니다. 작성자 및 관리자만 열람하실 수 있습니다."
            : "이 게시물의 정보를 볼 수 없습니다."}
        </p>
      ) : (
        <>
          <MyTitle title="내용보기" className="mb-4" />

          <div className="border-t-2 border-primary overflow-x-auto max-w-full">
            <table className="w-full border-collapse mb-5">
              <tbody>
                <tr>
                  <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                    작성일
                  </th>
                  <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                    {dayjs(data?.createdDate).format("YYYY-MM-DD")}
                  </td>
                </tr>
                <tr>
                  <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                    작성자
                  </th>
                  <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                    {data?.writer?.name || "-"}
                  </td>
                </tr>
                <tr>
                  <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                    접수자 E-mail
                  </th>
                  <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                    {data?.writer?.email || "-"}
                  </td>
                </tr>
                <tr>
                  <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                    내용
                  </th>
                  <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto whitespace-pre-wrap">
                    {data?.body}
                  </td>
                </tr>
                <tr>
                  <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                    다음글
                  </th>
                  <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                    {data?.nextItem ? (
                      <Link
                        to={`/ContactUs/${data.nextItem.itemNo}`}
                        className="text-gray-dark"
                      >
                        {data.nextItem?.title}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
                <tr>
                  <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                    이전글
                  </th>
                  <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                    {data?.prevItem ? (
                      <Link
                        to={`/ContactUs/${data.prevItem.itemNo}`}
                        className="text-gray-dark"
                      >
                        {data.prevItem?.title}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="flex items-center mt-10 sm:gap-4 gap-2">
        <MyButton
          variant="contained"
          color="#111111"
          className="font-semibold sm:min-w-[120px] mr-auto text-white min-w-[80px]"
          onClick={() => navigate(`/ContactUs`)}
        >
          목록보기
        </MyButton>

        <MyButton
          variant="outlined"
          className="sm:min-w-[120px] min-w-[80px]"
          onClick={openDeleteDialog}
        >
          <span className="font-semibold">삭제</span>
        </MyButton>
      </div>
    </>
  ) : (
    <div className="border-t-2 border-primary overflow-x-auto max-w-full">
      <p className="text-center text-lg text-gray-dark pt-10">
        이 내용을 볼 수 없습니다.
      </p>
    </div>
  );
};

export default ContactUsDetail;
